import { uuid } from '@shared/api/client'
import { messagesRepo, userDb, type LocalMessage, type SendMessageOp } from '@shared/db'
import { triggerQueue } from '@shared/sync/queue-runner'

/**
 * All 100 local copies and their durable send operations commit together.
 * A crash during preparation therefore leaves either the complete batch or none;
 * network retries reuse the committed client_message_id of each copy.
 */
export async function enqueueForwardBatch(
  source: readonly LocalMessage[],
  conversationId: string,
  senderId: string,
): Promise<void> {
  if (!source.length || source.length > 100) throw new Error('forward batch size must be 1..100')
  const ordered = [...source].sort((a, b) => a.seq - b.seq)
  if (
    new Set(ordered.map((item) => item.id)).size !== ordered.length ||
    ordered.some(
      (item) =>
        item.deleted ||
        item.id.startsWith('local:') ||
        !['text', 'image', 'video', 'voice', 'file'].includes(item.type),
    )
  )
    throw new Error('forward batch has an unavailable source')

  const db = userDb()
  await db.transaction('rw', db.messages, db.pending_ops, db.ui_state, async () => {
    for (const item of ordered) {
      const clientMessageId = uuid()
      const operation: SendMessageOp = {
        op_id: uuid(),
        kind: 'send_message',
        created_at: new Date().toISOString(),
        attempt_count: 0,
        next_attempt_at: null,
        last_error: null,
        claimed_by: null,
        claimed_at: null,
        idempotency_key: uuid(),
        conversation_id: conversationId,
        client_message_id: clientMessageId,
        forward_of: item.id,
      }
      await messagesRepo.enqueueLocal(
        {
          conversationId,
          clientMessageId,
          senderId,
          type: item.type as Parameters<typeof messagesRepo.enqueueLocal>[0]['type'],
          text: item.text,
          forwarded: true,
          forwardOf: item.id,
        },
        operation,
      )
    }
  })
  triggerQueue()
}
